/**
 * MODULE: _shared/frontend-shell/src/components/brain/brainLearnedPreferenceExplainUtils.ts
 * PURPOSE: Human-readable “why promoted” copy for self-learned assistant preferences.
 * DEPENDENCIES: none
 * PUBLIC_API: formatLearnedPreferenceExplanation, parseLearnedPreferenceTopic
 * SIDE_EFFECTS: none
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 88 / 150
 * RECENT: 2026-07-05 [2.2.1491] — Exact recurrence_count from API when available (not “3+” placeholder).
 * RECENT_PRIOR: 2026-07-05 [2.2.1490] — Explain promotion thresholds + topic scope for assistant rail and Settings.
 */

export type LearnedPreferenceExplainInput = {
  memory_key?: string | null;
  label?: string | null;
  memory_value?: string | null;
  confidence?: number | null;
  source?: string | null;
  recurrence_count?: number | null;
};

/** Topic slug from keys like `query_focus:deal_status|deal:uuid`. */
export function parseLearnedPreferenceTopic(memoryKey?: string | null): string | null {
  const key = (memoryKey || '').trim();
  if (!key.startsWith('query_focus:')) {
    return null;
  }
  const focus = key.slice('query_focus:'.length).split('|')[0]?.trim();
  if (!focus) {
    return null;
  }
  return focus.replace(/_/g, ' ');
}

function parseEntityScopeFromMemoryKey(memoryKey?: string | null): 'deal' | 'contact' | null {
  const key = (memoryKey || '').trim();
  if (!key.includes('|')) {
    return null;
  }
  const entityPart = key.split('|').pop() || '';
  const kind = entityPart.split(':')[0]?.toLowerCase();
  if (kind === 'deal') {
    return 'deal';
  }
  if (kind === 'contact') {
    return 'contact';
  }
  return null;
}

function formatRecurrenceLead(
  recurrenceCount: number | null | undefined,
  topic: string | null,
  label: string,
): string {
  const count =
    typeof recurrenceCount === 'number' && recurrenceCount > 0 ? recurrenceCount : null;
  if (topic && count) {
    return `Promoted after ${count} ${topic} question${count === 1 ? '' : 's'} in assistant chat`;
  }
  if (topic) {
    return `Promoted after 3+ ${topic} questions in assistant chat`;
  }
  if (count) {
    return `Promoted after ${count} similar assistant interaction${count === 1 ? '' : 's'}`;
  }
  if (/frequently/i.test(label)) {
    return 'Promoted after repeated assistant interactions (3+ similar turns)';
  }
  return 'Promoted into persistent memory from your usage';
}

/** One-line explainability for promoted patterns. */
export function formatLearnedPreferenceExplanation(input: LearnedPreferenceExplainInput): string {
  const label = (input.label || input.memory_value || '').trim();
  const topic = parseLearnedPreferenceTopic(input.memory_key);
  const entityScope = parseEntityScopeFromMemoryKey(input.memory_key);
  const confidence = typeof input.confidence === 'number' ? input.confidence : 0;

  const parts: string[] = [
    formatRecurrenceLead(input.recurrence_count, topic, label),
  ];

  if (entityScope === 'deal') {
    parts.push('scoped to one deal');
  } else if (entityScope === 'contact') {
    parts.push('scoped to one contact');
  }

  if (confidence >= 0.85) {
    parts.push('high confidence');
  } else if (confidence >= 0.65) {
    parts.push('meets promotion threshold');
  }

  parts.push('now shapes deal, account, and assistant answers');

  return parts.join(' · ');
}
